import React, { useRef } from 'react';
import { Box, FormControl, FormHelperText, FormLabel, Image, Text, VStack } from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';
import { OutlineButton } from './Buttons';

/**
 * Photo / document picker. Keeps the File for S3 upload.
 * value shape: { name, url, file } | null
 */
export function PhotoUploadField({
  label = 'Photo',
  value,
  onChange,
  helperText = 'Upload for now (capture / Aadhaar later).',
  accept = 'image/*,application/pdf',
}) {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (value?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(value.url);
    }
    const isImage = (file.type || '').startsWith('image/');
    const url = isImage ? URL.createObjectURL(file) : null;
    onChange?.({ name: file.name, url, file });
    e.target.value = '';
  };

  const clear = () => {
    if (value?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(value.url);
    }
    onChange?.(null);
  };

  const isPdf =
    (value?.file?.type || '').includes('pdf') ||
    /\.pdf$/i.test(value?.name || '');

  return (
    <FormControl>
      {label ? <FormLabel>{label}</FormLabel> : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={handleFile}
      />
      {value?.name ? (
        <Box
          position="relative"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="sm"
          overflow="hidden"
          bg="bg.input"
        >
          {value.url ? (
            <Image src={value.url} alt={value.name || 'Photo'} maxH="160px" w="100%" objectFit="cover" />
          ) : (
            <Box px={3} py={6} textAlign="center">
              <Text fontSize="13px" fontWeight="600">
                {isPdf ? 'PDF selected' : 'File selected'}
              </Text>
            </Box>
          )}
          <VStack align="stretch" spacing={1} p={3}>
            <Text fontSize="13px" noOfLines={1}>
              {value.name}
            </Text>
            <OutlineButton size="sm" leftIcon={<MdClose />} onClick={clear} type="button">
              Remove
            </OutlineButton>
          </VStack>
        </Box>
      ) : (
        <VStack
          as="button"
          type="button"
          onClick={() => inputRef.current?.click()}
          w="100%"
          minH="120px"
          justify="center"
          spacing={2}
          bg="bg.input"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="sm"
          color="text.primary"
          cursor="pointer"
          _hover={{ borderColor: 'utkarsh.amber' }}
        >
          <Text fontSize="24px" fontWeight="500" lineHeight="1">
            +
          </Text>
          <Text fontSize="13px" fontWeight="600">
            Upload file
          </Text>
        </VStack>
      )}
      {helperText ? <FormHelperText color="text.secondary">{helperText}</FormHelperText> : null}
    </FormControl>
  );
}
